import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  IconButton,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Add, CloseCircle, DocumentUpload, Edit2, Eye, EyeSlash, More, Trash } from 'iconsax-reactjs';

import {
  apiErrorMessage,
  apiValidationErrors,
  adminApi,
  bulkDeleteRecords,
  createRecord,
  CustomFieldDefinition,
  deleteAttachment,
  deleteRecord,
  fetchCustomFields,
  fetchRecord,
  fetchSelectOptions,
  fetchTable,
  isLoyaltyEnabled,
  SelectOption,
  updateRecord
} from '../api/client';
import { can, isSuperUser, resourcePermissions } from '../auth/permissions';
import { ensureLocation, onLocationChanged } from '../session/location';
import { useAuth } from '../components/AuthGate';
import { ErrorBlock, LoadingBlock } from '../components/StateBlock';
import { SectionHeader } from '../dashboard/components/SectionHeader';
import { InsightStrip, type InsightChip } from '../dashboard/components/InsightStrip';
import { EmptyState } from '../dashboard/components/EmptyState';
import { QrScanInput } from './QrScanInput';
import { formatDateAuto, isDateKey, isDateValue, t, tLabel } from '../i18n';
import { WorkspaceSection, WorkspaceFieldGrid } from '../workspace/WorkspaceSection';
import { WorkspaceSummary } from '../workspace/WorkspaceSummary';
import { validateField } from '../workspace/validateField';
import { Combobox } from '../workspace/Combobox';
import {
  CrudActionContext,
  CrudColumn,
  CrudDetailSection,
  CrudField,
  CrudHeaderAction,
  CrudRecord,
  CrudResourceConfig,
  CrudRowAction,
  CrudSubformField
} from './types';
import { UI } from '../brand/ui';

type FormErrors = Record<string, string[]>;

function defaultFormValue(field: CrudField) {
  if (field.defaultValue !== undefined) return field.defaultValue;
  if (field.type === 'checkbox') return false;
  if (field.type === 'multiselect' || field.type === 'subform') return [];
  return '';
}

function emptyForm(config: CrudResourceConfig) {
  return config.fields.reduce<Record<string, unknown>>((payload, field) => {
    payload[field.name] = defaultFormValue(field);
    return payload;
  }, {});
}

function displayCell(row: CrudRecord, key: string) {
  const value = row[key];
  if (value == null || value === '') return '-';
  if (typeof value === 'boolean') return value ? t('status.yes') : t('status.no');
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  // Locale-aware date formatting: if the key looks like a date field or the
  // value looks like a date/datetime string, format it.
  if (isDateKey(key) || isDateValue(value)) return formatDateAuto(value);
  return String(value);
}

function detailValue(row: CrudRecord, field: { key: string; render?: (row: CrudRecord) => string }) {
  if (field.render) return field.render(row);
  return displayCell(row, field.key);
}

function displayRecordTitle(row: CrudRecord | null, fallback: string) {
  if (!row) return fallback;
  const name = displayCell(row, 'name');
  if (name !== '-') return name;
  const title = displayCell(row, 'title');
  if (title !== '-') return title;
  return displayCell(row, 'code') !== '-' ? displayCell(row, 'code') : fallback;
}

function rowMatchesQuery(row: CrudRecord, config: CrudResourceConfig, rawQuery: string) {
  const queryText = rawQuery.trim().toLowerCase();
  if (!queryText) return true;

  const [, explicitValue] = queryText.includes(':') ? queryText.split(/:(.*)/s) : queryText.includes('=') ? queryText.split(/=(.*)/s) : [];
  const needle = (explicitValue || queryText).trim();
  const keys = Array.from(new Set([config.searchField, ...config.columns.map((column) => column.key), ...config.fields.map((field) => field.name)].filter(Boolean)));

  return keys.some((key) => {
    const value = row[key as string];
    if (value == null) return false;
    if (Array.isArray(value)) return value.join(' ').toLowerCase().includes(needle);
    if (typeof value === 'object') return JSON.stringify(value).toLowerCase().includes(needle);
    return String(value).toLowerCase().includes(needle);
  });
}

function numericValue(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function moneySummary(value: number) {
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

type OptionBackedField = Pick<CrudField | CrudSubformField, 'name' | 'options' | 'optionsEndpoint' | 'includeAllOption'>;
type ItemSearchResult = Record<string, unknown> & { id: string | number; label?: string; name?: string; code?: string };

function optionBackedFields(fields: CrudField[]): OptionBackedField[] {
  return fields.flatMap((field) => [field, ...(field.subformFields ?? [])]).filter((field) => Boolean(field.optionsEndpoint));
}

function withConfiguredOptions(field: OptionBackedField, loadedOptions: SelectOption[]) {
  const configured = field.options ?? [];
  const merged = [...configured, ...loadedOptions];
  if (!field.includeAllOption || merged.some((option) => option.value === 'all')) return merged;
  return [{ label: t('common.all'), value: 'all' }, ...merged];
}

function normalizeAllSelection(previousValue: unknown, nextValue: unknown) {
  const previous = Array.isArray(previousValue) ? previousValue.map(String) : [];
  const raw = Array.isArray(nextValue) ? nextValue : typeof nextValue === 'string' ? nextValue.split(',') : [];
  const next = raw.map(String).filter(Boolean);
  if (!next.includes('all')) return next;
  if (!previous.includes('all') || next.length === 1) return ['all'];
  return next.filter((value) => value !== 'all');
}

function itemSearchLabel(item: ItemSearchResult) {
  return String(item.label ?? (item.name && item.code ? `${item.name} (${item.code})` : item.name ?? item.code ?? item.id));
}

function itemLineFromRecord(item: Record<string, unknown>) {
  const locationStock = Array.isArray(item.location_stock) ? (item.location_stock[0] as Record<string, unknown> | undefined) : undefined;
  const taxes = Array.isArray(item.taxes) ? item.taxes : [];
  const cost = numericValue(locationStock?.cost ?? item.cost);
  return {
    ...item,
    item_id: item.id,
    code: item.code ?? '',
    name: item.name ?? '',
    cost,
    unit_cost: cost,
    net_cost: cost,
    quantity: 1,
    discount: null,
    discount_amount: null,
    taxes,
    allTaxes: taxes
  };
}

async function ensureSessionLocationSelected() {
  await ensureLocation();
}

// Compute resource-specific insight chips from loaded rows. Gives business context
// before the table (the "page philosophy"). Falls back to a generic count chip.
function computeInsights(resource: string, rows: CrudRecord[], count: number): InsightChip[] {
  const chips: InsightChip[] = [{ label: t('crud.total'), value: count }];
  const numField = (r: CrudRecord, f: string) => numericValue(r[f]);
  const sum = (field: string) => rows.reduce((a, r) => a + numField(r, field), 0);

  switch (resource) {
    case 'items':
      chips.push({ label: t('crud.inventoryValue'), value: Math.round(rows.reduce((a, r) => a + numField(r, 'cost') * numericValue((r.location_stock as Record<string, unknown> | undefined)?.quantity), 0)).toLocaleString() });
      break;
    case 'sales':
    case 'quotations':
    case 'recurring_sales':
    case 'return_orders':
      chips.push({ label: t('crud.value'), value: Math.round(sum('grand_total')).toLocaleString() });
      chips.push({ label: t('crud.outstanding'), value: Math.round(rows.reduce((a, r) => {
        const isDraft = numField(r, 'draft') !== 0;
        const isPaid = numField(r, 'paid') !== 0;
        return isDraft || isPaid ? a : a + numField(r, 'grand_total');
      }, 0)).toLocaleString(), tone: 'warning' });
      break;
    case 'purchases':
      chips.push({ label: t('crud.value'), value: Math.round(sum('grand_total')).toLocaleString() });
      break;
    case 'accounts':
    case 'gift_cards':
      chips.push({ label: t('crud.balance'), value: Math.round(sum('amount')).toLocaleString() });
      break;
    case 'payments':
    case 'incomes':
    case 'expenses':
      chips.push({ label: t('field.amount'), value: Math.round(sum('amount')).toLocaleString() });
      break;
    case 'customers':
    case 'suppliers':
      chips.push({ label: t('crud.rowsLoaded'), value: rows.length });
      break;
  }
  return chips;
}

// Translate a config-provided label that may be an i18n key ("action.email")
// or a literal ("Email").
function tAction(label: string) {
  if (/^[a-zA-Z]+\.[a-zA-Z0-9_.]+$/.test(label)) return t(label);
  return tLabel(label);
}

function isFieldVisible(field: CrudField, form: Record<string, unknown>, editing: boolean) {
  if (field.createOnly && editing) return false;
  if (field.visibleWhen && !field.visibleWhen(form, { editing })) return false;
  return true;
}

// Values that must not be sent back to the API: existing file URLs (the
// backend calls ->store() on whatever arrives under a file key), fields hidden
// by createOnly while editing.
function stripUnsendable(config: CrudResourceConfig, payload: Record<string, unknown>, editing: boolean) {
  const next = { ...payload };
  config.fields.forEach((field) => {
    if (field.createOnly && editing) delete next[field.name];
    if (field.type === 'file' && !(typeof File !== 'undefined' && next[field.name] instanceof Blob)) delete next[field.name];
    if (field.type === 'files') {
      const files = Array.isArray(next[field.name]) ? (next[field.name] as unknown[]).filter((f) => typeof File !== 'undefined' && f instanceof Blob) : [];
      if (files.length) next[field.name] = files;
      else delete next[field.name];
    }
  });
  return next;
}

function normalizeExtraAttributes(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  return {};
}

type DialogEntry = { id: number; title?: string; maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; content: (close: () => void) => ReactNode };
type ConfirmEntry = { message: string; title?: string; confirmLabel?: string; color?: 'error' | 'warning' | 'primary'; resolve: (ok: boolean) => void };

export function CrudResourcePage({ config }: { config: CrudResourceConfig }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // The API endpoint for CRUD calls — defaults to config.resource but can be
  // overridden (e.g. 'items-services' → 'items') so sub-type views hit the
  // same backend resource while presenting as separate pages.
  const apiResource = config.apiResource ?? config.resource;
  const permissions = resourcePermissions(config.permissionResource ?? config.resource);
  const canRead = can(user, permissions.read);
  const canCreate = !config.readOnly && !config.disableCreate && can(user, permissions.create);
  const canUpdate = !config.readOnly && !config.disableUpdate && can(user, permissions.update);
  const canDelete = !config.readOnly && !config.disableDelete && (!config.deleteSuperOnly || isSuperUser(user)) && can(user, permissions.delete);
  const canBulkDelete = Boolean(config.bulkDelete) && canDelete;

  const [rows, setRows] = useState<CrudRecord[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState((config.pageSizes ?? [10])[0] ?? 10);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>(() => emptyForm(config));
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [options, setOptions] = useState<Record<string, SelectOption[]>>({});
  const [dependentOptions, setDependentOptions] = useState<Record<string, { query: string; options: SelectOption[] }>>({});
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' | 'warning' | 'info' } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CrudRecord | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [finalizingId, setFinalizingId] = useState<string | number | null>(null);
  const [loyaltyOn, setLoyaltyOn] = useState<boolean | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState<CrudRecord | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [forceCloseTarget, setForceCloseTarget] = useState<CrudRecord | null>(null);
  const [forceCloseComment, setForceCloseComment] = useState('');
  const [forceCloseError, setForceCloseError] = useState<string | null>(null);
  const [forceClosing, setForceClosing] = useState(false);
  const [customFormState, setCustomFormState] = useState<{ recordId: string | number | null; prefill?: Record<string, unknown> } | null>(null);
  const [dialogs, setDialogs] = useState<DialogEntry[]>([]);
  const [confirmEntry, setConfirmEntry] = useState<ConfirmEntry | null>(null);
  const [actionMenu, setActionMenu] = useState<{ anchor: HTMLElement; row: CrudRecord } | null>(null);
  const [runningAction, setRunningAction] = useState<string | null>(null);

  const listFilters = config.listFilters ?? [];
  const filterValues = useMemo(() => {
    const values: Record<string, string> = {};
    listFilters.forEach((filter) => {
      const value = searchParams.get(filter.name) ?? filter.defaultValue ?? '';
      if (value !== '') values[filter.name] = value;
    });
    (config.passthroughQueryParams ?? []).forEach((name) => {
      const value = searchParams.get(name);
      if (value != null && value !== '') values[name] = value;
    });
    return values;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, config.resource]);
  const filterKey = JSON.stringify(filterValues);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query), 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  const showToast = useCallback((message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => setToast({ message, severity }), []);

  const loadRows = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    const orderBy = sort ? `${sort.key} ${sort.dir}` : config.orderBy;
    const extra = { ...(config.listFilter ?? {}), ...filterValues };
    try {
      if (config.searchMode === 'local') {
        const response = await fetchTable<CrudRecord>(apiResource, {
          page: 1,
          limit: config.localSearchLimit ?? 100,
          orderBy,
          extra
        });
        const filtered = (response.data ?? []).filter((row) => rowMatchesQuery(row, config, debouncedQuery));
        const start = page * limit;
        setRows(filtered.slice(start, start + limit));
        setCount(filtered.length);
        return;
      }

      const searchQuery = debouncedQuery && config.searchField && !debouncedQuery.includes(':') && !debouncedQuery.includes('=') ? `${config.searchField}:${debouncedQuery}` : debouncedQuery;
      const response = await fetchTable<CrudRecord>(apiResource, {
        page: page + 1,
        limit,
        query: searchQuery,
        orderBy,
        extra
      });
      setRows(Array.isArray(response.data) ? response.data : []);
      setCount(response.count ?? 0);
    } catch (err) {
      setError(apiErrorMessage(err, t('common.unableToLoad', { name: tLabel(config.title) })));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead, config, limit, page, debouncedQuery, sort, filterKey, apiResource]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  // Location-scoped lists (sales, purchases…) follow the header location switcher.
  useEffect(() => onLocationChanged(() => void loadRows()), [loadRows]);

  useEffect(() => {
    setSelected(new Set());
  }, [rows]);

  // When the resource is sales with finalize enabled, check once whether loyalty
  // is enabled on the tenant — sales finalize is hard-gated on loyalty being OFF
  // (the legacy sale-reversal path has a known bug when loyalty is configured).
  // Other finalize-enabled resources (adjustments, transfers) are not loyalty-gated.
  useEffect(() => {
    if (!config.canFinalize || config.resource !== 'sales') return;
    isLoyaltyEnabled().then(setLoyaltyOn).catch(() => setLoyaltyOn(true));
  }, [config.canFinalize, config.resource]);

  useEffect(() => {
    const fields = optionBackedFields(config.fields);
    fields.forEach((field) => {
      if (!field.optionsEndpoint || options[field.name]) return;
      fetchSelectOptions(field.optionsEndpoint)
        .then((items) => setOptions((current) => ({ ...current, [field.name]: items })))
        .catch(() => setOptions((current) => ({ ...current, [field.name]: [] })));
    });
    listFilters.forEach((filter) => {
      const key = `filter:${filter.name}`;
      if (!filter.optionsEndpoint || options[key]) return;
      fetchSelectOptions(filter.optionsEndpoint)
        .then((items) => setOptions((current) => ({ ...current, [key]: items })))
        .catch(() => setOptions((current) => ({ ...current, [key]: [] })));
    });
  }, [config.fields, listFilters, options]);

  // Dependent options (e.g. states?country=XX) refresh when their query changes.
  useEffect(() => {
    if (!dialogOpen) return;
    config.fields.forEach((field) => {
      if (!field.optionsQuery) return;
      const endpoint = field.optionsQuery(form);
      const current = dependentOptions[field.name];
      if (!endpoint) {
        if (current && current.query !== '') setDependentOptions((prev) => ({ ...prev, [field.name]: { query: '', options: [] } }));
        return;
      }
      if (current?.query === endpoint) return;
      setDependentOptions((prev) => ({ ...prev, [field.name]: { query: endpoint, options: prev[field.name]?.options ?? [] } }));
      fetchSelectOptions(endpoint)
        .then((items) => setDependentOptions((prev) => ({ ...prev, [field.name]: { query: endpoint, options: items } })))
        .catch(() => setDependentOptions((prev) => ({ ...prev, [field.name]: { query: endpoint, options: [] } })));
    });
  }, [config.fields, dependentOptions, dialogOpen, form]);

  useEffect(() => {
    if (!config.customFields) return;
    fetchCustomFields(apiResource).then(setCustomFieldDefs).catch(() => setCustomFieldDefs([]));
  }, [apiResource, config.customFields]);

  const openCreate = useCallback((prefill?: Record<string, unknown>) => {
    if (config.customForm) {
      setCustomFormState({ recordId: null, prefill });
      return;
    }
    setEditingId(null);
    setForm({ ...emptyForm(config), extra_attributes: {}, ...(prefill ?? {}) });
    setFormErrors({});
    setTouchedFields({});
    setNotice(null);
    setDialogOpen(true);
  }, [config]);

  const openEdit = useCallback(async (row: CrudRecord) => {
    if (config.customForm) {
      setCustomFormState({ recordId: row.id });
      return;
    }
    setEditingId(row.id);
    setFormErrors({});
    setTouchedFields({});
    setNotice(null);
    setDialogOpen(true);
    try {
      const record = await fetchRecord<CrudRecord>(apiResource, row.id);
      const normalized = config.normalizeAfterLoad ? config.normalizeAfterLoad(record) : record;
      setForm({ ...emptyForm(config), ...normalized, extra_attributes: normalizeExtraAttributes(normalized.extra_attributes) });
    } catch (err) {
      setNotice(apiErrorMessage(err, t('common.unableToLoad', { name: tLabel(config.singular) })));
    }
  }, [apiResource, config]);

  const openDetail = useCallback(async (row: CrudRecord) => {
    setDetailOpen(true);
    setDetailRecord(row);
    setDetailError(null);
    setDetailLoading(true);
    try {
      const record = await fetchRecord<CrudRecord>(apiResource, row.id);
      setDetailRecord(config.normalizeAfterLoad ? config.normalizeAfterLoad(record) : record);
    } catch (err) {
      setDetailError(apiErrorMessage(err, t('common.unableToLoad', { name: tLabel(config.singular) })));
    } finally {
      setDetailLoading(false);
    }
  }, [apiResource, config]);

  const openDialog = useCallback((content: DialogEntry['content'], options?: { title?: string; maxWidth?: DialogEntry['maxWidth'] }) => {
    const id = Date.now() + Math.random();
    setDialogs((current) => [...current, { id, content, title: options?.title, maxWidth: options?.maxWidth }]);
    return () => setDialogs((current) => current.filter((entry) => entry.id !== id));
  }, []);

  const confirm = useCallback((message: string, options?: { title?: string; confirmLabel?: string; color?: 'error' | 'warning' | 'primary' }) => {
    return new Promise<boolean>((resolve) => setConfirmEntry({ message, ...options, resolve }));
  }, []);

  const actionContext: CrudActionContext = useMemo(() => ({
    navigate: (to: string) => navigate(to),
    reload: loadRows,
    toast: showToast,
    openCreate,
    openEdit: (row: CrudRecord) => void openEdit(row),
    openDetail: (row: CrudRecord) => void openDetail(row),
    openDialog,
    confirm,
    user,
    config
  }), [config, confirm, loadRows, navigate, openCreate, openDetail, openDialog, openEdit, showToast, user]);

  // Deep links: ?create=1&field=value pre-fills the create form (e.g. a payment
  // for a sale), ?view=<id> opens the detail drawer, ?edit=<id> the editor.
  useEffect(() => {
    const create = searchParams.get('create');
    const view = searchParams.get('view');
    const edit = searchParams.get('edit');
    if (!create && !view && !edit) return;
    const reserved = new Set(['create', 'view', 'edit', ...listFilters.map((f) => f.name), ...(config.passthroughQueryParams ?? [])]);
    const next = new URLSearchParams(searchParams);
    if (create && canCreate) {
      const prefill: Record<string, unknown> = {};
      searchParams.forEach((value, key) => {
        if (!reserved.has(key)) {
          prefill[key] = value;
          next.delete(key);
        }
      });
      openCreate(prefill);
    }
    if (view) void openDetail({ id: view });
    if (edit && canUpdate) void openEdit({ id: edit });
    next.delete('create');
    next.delete('view');
    next.delete('edit');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const updateField = (name: string, value: unknown) => {
    setForm((current) => ({ ...current, [name]: value }));
    setTouchedFields((touched) => ({ ...touched, [name]: true }));
    // Clear server error for this field when the user edits.
    setFormErrors((errs) => (errs[name] ? { ...errs, [name]: [] } : errs));
  };

  const updateCustomField = (slug: string, value: unknown) => {
    setForm((current) => ({ ...current, extra_attributes: { ...normalizeExtraAttributes(current.extra_attributes), [slug]: value } }));
  };

  const save = async () => {
    setSaving(true);
    setFormErrors({});
    setNotice(null);
    let payload: Record<string, unknown>;
    try {
      payload = config.normalizeBeforeSave ? config.normalizeBeforeSave(form) : form;
      payload = stripUnsendable(config, payload, Boolean(editingId));
      if (config.customFields) {
        const extras = normalizeExtraAttributes(form.extra_attributes);
        if (customFieldDefs.length) payload = { ...payload, extra_attributes: extras };
      } else if (Array.isArray(payload.extra_attributes)) {
        delete payload.extra_attributes;
      }
    } catch (e) {
      setSaving(false);
      const msg = e instanceof Error ? e.message : String(e);
      setNotice(msg);
      setToast({ message: msg, severity: 'error' });
      return;
    }
    try {
      if (config.requiresLocation) await ensureLocation();
      const response = editingId
        ? await updateRecord<CrudRecord>(apiResource, editingId, payload)
        : await createRecord<CrudRecord>(apiResource, payload);
      if (response && response.success === false) {
        setNotice(response.message ?? t('common.saveFailed'));
        setToast({ message: response.message ?? t('common.saveFailed'), severity: 'error' });
        return;
      }
      setDialogOpen(false);
      setToast({ message: `${tLabel(config.singular)} ${editingId ? t('action.updated') : t('action.created')}.`, severity: 'success' });
      await loadRows();
    } catch (err) {
      const validationErrors = apiValidationErrors(err);
      const hasValidationErrors = Object.keys(validationErrors).length > 0;
      setFormErrors(validationErrors);
      setNotice(hasValidationErrors ? null : apiErrorMessage(err, t('common.saveFailed')));
      setToast({ message: hasValidationErrors ? t('common.fixHighlighted') : apiErrorMessage(err, t('common.saveFailed')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Cmd/Ctrl+S keyboard shortcut to save (only when dialog is open).
  useEffect(() => {
    if (!dialogOpen) return;
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (!saving) void save();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, saving]);

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setNotice(null);
    try {
      const response = await deleteRecord(apiResource, deleteTarget.id);
      if (!response.success) {
        const message = (response as { message?: string; error?: string }).message ?? (response as { error?: string }).error ?? t('common.deleteFailed');
        setNotice(message);
        setToast({ message, severity: 'error' });
        return;
      }
      setToast({ message: t('common.deleted', { name: tLabel(config.singular) }), severity: 'success' });
      setDeleteTarget(null);
      if (detailRecord && String(detailRecord.id) === String(deleteTarget.id)) setDetailOpen(false);
      await loadRows();
    } catch (err) {
      setNotice(apiErrorMessage(err, t('common.deleteFailed')));
      setToast({ message: apiErrorMessage(err, t('common.deleteFailed')), severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const removeSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setDeleting(true);
    try {
      const response = await bulkDeleteRecords(apiResource, ids);
      setToast({
        message: t('crud.bulkDeleted', { count: response.count ?? 0, failed: response.failed ?? 0 }),
        severity: response.success ? 'success' : 'warning'
      });
      setBulkConfirmOpen(false);
      await loadRows();
    } catch (err) {
      setToast({ message: apiErrorMessage(err, t('common.deleteFailed')), severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // Finalize flips a draft record to its posted/finalized state. For sales this
  // is draft→0 (triggering stock deduction + journal posting + costing cascade);
  // for stock transfers it's status→'transferred' (moving stock between locations).
  // The field and value are config-driven; sales are hard-gated on loyalty being OFF.
  const finalizeRecord = async (row: CrudRecord) => {
    const finalizeField = config.finalizeField ?? 'draft';
    const finalizeValue = config.finalizeValue ?? 0;
    const isSale = config.resource === 'sales';
    if (isSale && loyaltyOn) return;
    setFinalizingId(row.id);
    setNotice(null);
    try {
      if (config.requiresLocation) await ensureLocation();
      // Load the full record, then PUT it back with the finalize field flipped.
      const record = await fetchRecord<CrudRecord>(apiResource, row.id);
      const loaded = config.normalizeAfterLoad ? config.normalizeAfterLoad(record) : record;
      const finalizePayload = { ...loaded, [finalizeField]: finalizeValue };
      const payload = config.normalizeBeforeSave ? config.normalizeBeforeSave(finalizePayload) : finalizePayload;
      await updateRecord(apiResource, row.id, stripUnsendable(config, payload, true));
      setToast({ message: t('common.finalizeSuccess', { name: tLabel(config.singular) }), severity: 'success' });
      await loadRows();
    } catch (err) {
      setNotice(apiErrorMessage(err, t('common.finalizeFailed')));
      setToast({ message: apiErrorMessage(err, t('common.finalizeFailed')), severity: 'error' });
    } finally {
      setFinalizingId(null);
    }
  };

  const runRowAction = async (action: CrudRowAction, row: CrudRecord) => {
    setActionMenu(null);
    if (action.confirm) {
      const message = typeof action.confirm === 'function' ? action.confirm(row) : tAction(action.confirm);
      const ok = await confirm(message, { title: tAction(action.label), color: action.color === 'error' ? 'error' : 'primary' });
      if (!ok) return;
    }
    setRunningAction(`${action.key}:${row.id}`);
    try {
      await action.run(row, actionContext);
    } catch (err) {
      setToast({ message: apiErrorMessage(err, t('common.actionFailed')), severity: 'error' });
    } finally {
      setRunningAction(null);
    }
  };

  const runHeaderAction = async (action: CrudHeaderAction) => {
    if (action.confirm) {
      const ok = await confirm(tAction(action.confirm), { title: tAction(action.label) });
      if (!ok) return;
    }
    setRunningAction(`header:${action.key}`);
    try {
      await action.run(actionContext);
    } catch (err) {
      setToast({ message: apiErrorMessage(err, t('common.actionFailed')), severity: 'error' });
    } finally {
      setRunningAction(null);
    }
  };

  const openForceClose = (row: CrudRecord) => {
    setForceCloseTarget(row);
    setForceCloseComment('');
    setForceCloseError(null);
  };

  const closeForceCloseDialog = () => {
    if (forceClosing) return;
    setForceCloseTarget(null);
    setForceCloseComment('');
    setForceCloseError(null);
  };

  const forceCloseRegister = async () => {
    if (!forceCloseTarget) return;
    setForceClosing(true);
    setForceCloseError(null);
    try {
      await adminApi.post(`pos/register/${forceCloseTarget.id}/force-close`, { comment: forceCloseComment });
      setToast({ message: t('registerRecords.forceCloseSuccess'), severity: 'success' });
      setForceCloseTarget(null);
      setForceCloseComment('');
      await loadRows();
    } catch (err) {
      setForceCloseError(apiErrorMessage(err, t('registerRecords.forceCloseFailed')));
    } finally {
      setForceClosing(false);
    }
  };

  const setFilter = (name: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === '') next.delete(name);
    else next.set(name, value);
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const clearFilters = () => {
    const next = new URLSearchParams(searchParams);
    listFilters.forEach((filter) => next.delete(filter.name));
    (config.passthroughQueryParams ?? []).forEach((name) => next.delete(name));
    setSearchParams(next, { replace: true });
    setPage(0);
  };

  const toggleSort = (column: CrudColumn) => {
    const key = typeof column.sortable === 'string' ? column.sortable : column.key;
    setSort((current) => {
      if (!current || current.key !== key) return { key, dir: 'asc' };
      if (current.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
    setPage(0);
  };

  const visibleRowActions = (row: CrudRecord) =>
    (config.rowActions ?? []).filter((action) => (!action.permission || can(user, action.permission)) && (!action.visible || action.visible(row)));
  const visibleHeaderActions = (config.headerActions ?? []).filter(
    (action) => (!action.permission || can(user, action.permission)) && (!action.superOnly || isSuperUser(user))
  );

  const localizedTitle = tLabel(config.title);
  const localizedSingular = tLabel(config.singular);

  // Compute insight chips from loaded rows — gives business context before the table.
  const insights: InsightChip[] = useMemo(() => computeInsights(config.resource, rows, count), [config.resource, rows, count]);
  const readOnlySummary = useMemo(() => {
    if (!config.readOnly) return [];
    const hasGrandTotal = rows.some((row) => row.grand_total != null);
    const hasAmount = rows.some((row) => row.amount != null);
    const hasPaid = rows.some((row) => row.paid != null);
    const hasItems = rows.some((row) => row.total_items != null);
    const grandTotal = rows.reduce((sum, row) => sum + numericValue(row.grand_total), 0);
    const amount = rows.reduce((sum, row) => sum + numericValue(row.amount), 0);
    // 'paid' is a boolean flag (tinyint), not a monetary amount — count paid rows.
    const paidCount = rows.filter((row) => numericValue(row.paid) !== 0).length;
    const items = rows.reduce((sum, row) => sum + numericValue(row.total_items), 0);
    return [
      { label: t('crud.records'), value: String(count) },
      { label: t('crud.loadedRows'), value: String(rows.length) },
      hasGrandTotal ? { label: t('crud.grandTotal'), value: moneySummary(grandTotal) } : null,
      hasAmount ? { label: t('field.amount'), value: moneySummary(amount) } : null,
      hasPaid ? { label: t('crud.paidCount'), value: String(paidCount) } : null,
      hasItems ? { label: t('crud.totalItems'), value: String(items) } : null
    ].filter((metric): metric is { label: string; value: string } => Boolean(metric));
  }, [config.readOnly, count, rows]);
  const formErrorSummary = Object.values(formErrors).flat();
  const editing = Boolean(editingId);
  const visibleFields = useMemo(() => config.fields.filter((field) => isFieldVisible(field, form, editing)), [config.fields, editing, form]);
  const hasPendingOptions = dialogOpen && optionBackedFields(visibleFields).some((field) => field.optionsEndpoint && !options[field.name]);
  const fieldsBySection = useMemo(() => groupFields(visibleFields), [visibleFields]);
  const deleteLabel = deleteTarget
    ? displayCell(deleteTarget, 'name') !== '-'
      ? displayCell(deleteTarget, 'name')
      : displayCell(deleteTarget, 'title') !== '-'
        ? displayCell(deleteTarget, 'title')
        : displayCell(deleteTarget, 'reference')
    : '';
  const detailTitle = displayRecordTitle(detailRecord, localizedSingular);
  const hasActiveFilters = Object.keys(filterValues).length > 0;
  const allSelected = rows.length > 0 && rows.every((row) => selected.has(String(row.id)));
  const colSpan = config.columns.length + 1 + (canBulkDelete ? 1 : 0);

  if (!canRead) {
    return <ErrorBlock message={t('crud.permissionDenied', { permission: permissions.read })} />;
  }

  const CustomForm = config.customForm;
  const DetailExtra = config.detailExtra;

  return (
    <Stack spacing={2.5}>
      <SectionHeader
        title={localizedTitle}
        description={config.description ? t(config.description) : config.readOnly ? t('common.readOnly', { title: localizedTitle.toLowerCase() }) : t('common.manage', { title: localizedTitle.toLowerCase() })}
        insights={insights}
        actionLabel={canCreate ? `${t('action.add')} ${localizedSingular}` : undefined}
        actionIcon={canCreate ? Add : undefined}
        onAction={canCreate ? () => openCreate() : undefined}
      />

      {/* Filters + search + header actions */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap" useFlexGap>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {listFilters.map((filter) => {
            const value = filterValues[filter.name] ?? '';
            if (filter.type === 'toggle') {
              const onValue = filter.onValue ?? '1';
              return (
                <FormControlLabel
                  key={filter.name}
                  control={<Switch size="small" checked={value === onValue} onChange={(event) => setFilter(filter.name, event.target.checked ? onValue : '')} />}
                  label={tAction(filter.label)}
                />
              );
            }
            if (filter.type === 'select') {
              const filterOptions = filter.options ?? options[`filter:${filter.name}`] ?? [];
              return (
                <TextField
                  key={filter.name}
                  select
                  size="small"
                  label={tAction(filter.label)}
                  value={value}
                  onChange={(event) => setFilter(filter.name, event.target.value)}
                  sx={{ minWidth: 180 }}
                >
                  <MenuItem value="">{t('common.all')}</MenuItem>
                  {filterOptions.map((option) => (
                    <MenuItem key={String(option.value)} value={String(option.value)}>
                      {tLabel(option.label)}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }
            return (
              <TextField
                key={filter.name}
                size="small"
                type={filter.type === 'date' ? 'date' : 'text'}
                label={tAction(filter.label)}
                value={value}
                onChange={(event) => setFilter(filter.name, event.target.value)}
                InputLabelProps={filter.type === 'date' ? { shrink: true } : undefined}
                sx={{ minWidth: 160 }}
              />
            );
          })}
          {hasActiveFilters ? (
            <Button size="small" onClick={clearFilters}>
              {t('action.clearFilters')}
            </Button>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" flexWrap="wrap" useFlexGap>
          {visibleHeaderActions.map((action) => {
            const Icon = action.icon;
            return (
              <Button
                key={action.key}
                variant={action.variant ?? 'outlined'}
                startIcon={Icon ? <Icon size={18} /> : undefined}
                disabled={runningAction === `header:${action.key}`}
                onClick={() => void runHeaderAction(action)}
              >
                {runningAction === `header:${action.key}` ? <CircularProgress size={18} /> : tAction(action.label)}
              </Button>
            );
          })}
          <TextField
            size="small"
            label={t('action.search')}
            value={query}
            onChange={(event) => {
              setPage(0);
              setQuery(event.target.value);
            }}
          />
          <Button variant="outlined" onClick={() => void loadRows()}>
            {loading ? <CircularProgress size={18} /> : t('action.refresh')}
          </Button>
        </Stack>
      </Stack>

      <InsightStrip chips={insights} loading={loading} />

      {notice ? <Alert severity="warning">{notice}</Alert> : null}
      {error ? <ErrorBlock message={error} /> : null}
      {config.readOnly && !loading ? (
        <Stack spacing={1.5}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${Math.min(Math.max(readOnlySummary.length, 1), 5)}, 1fr)` },
              gap: 1.5
            }}
          >
            {readOnlySummary.map((metric) => (
              <Box
                key={metric.label}
                sx={{
                  bgcolor: UI.white,
                  border: `1px solid ${UI.border}`,
                  borderRadius: '10px',
                  p: 2,
                  boxShadow: UI.shadowSm
                }}
              >
                <Typography color="text.secondary" variant="body2">
                  {metric.label}
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700 }}>
                  {metric.value}
                </Typography>
              </Box>
            ))}
          </Box>
          {!count ? <Alert severity="info">{t('crud.noTenantRows', { title: localizedTitle.toLowerCase() })}</Alert> : null}
        </Stack>
      ) : null}

      {canBulkDelete && selected.size > 0 ? (
        <Alert
          severity="info"
          action={
            <Button color="error" size="small" variant="contained" onClick={() => setBulkConfirmOpen(true)} disabled={deleting}>
              {t('action.deleteSelected')}
            </Button>
          }
        >
          {t('crud.selectedCount', { count: selected.size })}
        </Alert>
      ) : null}

      {loading ? (
        <LoadingBlock />
      ) : (
        <Box
          sx={{
            bgcolor: UI.white,
            border: `1px solid ${UI.border}`,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: UI.shadowSm
          }}
        >
          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {canBulkDelete ? (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      indeterminate={selected.size > 0 && !allSelected}
                      onChange={(event) => setSelected(event.target.checked ? new Set(rows.map((row) => String(row.id))) : new Set())}
                    />
                  </TableCell>
                ) : null}
                {config.columns.map((column) => {
                  const sortKey = typeof column.sortable === 'string' ? column.sortable : column.key;
                  return (
                    <TableCell key={column.key} align={column.align} sx={{ whiteSpace: 'nowrap' }}>
                      {column.sortable ? (
                        <TableSortLabel active={sort?.key === sortKey} direction={sort?.key === sortKey ? sort.dir : 'asc'} onClick={() => toggleSort(column)}>
                          {tLabel(column.label)}
                        </TableSortLabel>
                      ) : (
                        tLabel(column.label)
                      )}
                    </TableCell>
                  );
                })}
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {t('action.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const rowTone = config.rowTone?.(row);
                const extraActions = visibleRowActions(row);
                return (
                <TableRow
                  key={row.id}
                  hover
                  selected={selected.has(String(row.id))}
                  onDoubleClick={config.detailSections || DetailExtra ? () => void openDetail(row) : undefined}
                  sx={{
                    '&:hover': { bgcolor: UI.accentAlpha(0.035) },
                    bgcolor: rowTone === 'error' ? UI.dangerTint : rowTone === 'warning' ? UI.warningTint : rowTone === 'success' ? UI.successTint : undefined
                  }}
                >
                {canBulkDelete ? (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selected.has(String(row.id))}
                      onChange={(event) =>
                        setSelected((current) => {
                          const next = new Set(current);
                          if (event.target.checked) next.add(String(row.id));
                          else next.delete(String(row.id));
                          return next;
                        })
                      }
                    />
                  </TableCell>
                ) : null}
                {config.columns.map((column) => {
                  const cellContent = column.render ? column.render(row) : displayCell(row, column.key);
                  const tone = column.tone?.(row);
                  // Render a color swatch for color columns.
                  const isColor = column.key === 'color' && cellContent && cellContent !== '-';
                  return (
                    <TableCell
                      key={column.key}
                      align={column.align}
                      sx={{
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: tone && tone !== 'default' ? 700 : undefined,
                        color: tone === 'error' ? 'error.main' : tone === 'warning' ? 'warning.dark' : tone === 'success' ? 'success.dark' : undefined,
                        bgcolor: tone === 'error' ? 'error.50' : tone === 'warning' ? 'warning.50' : tone === 'success' ? 'success.50' : undefined
                      }}
                    >
                      {isColor ? (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 18, height: 18, borderRadius: '4px', bgcolor: cellContent, border: 1, borderColor: 'divider', flexShrink: 0 }} component="span" />
                          {cellContent}
                        </Box>
                      ) : cellContent}
                    </TableCell>
                  );
                })}
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      {config.detailSections || DetailExtra ? (
                        <Tooltip title={t('action.view')} placement="top">
                          <IconButton size="small" onClick={() => void openDetail(row)}>
                            <Eye size={18} />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {canUpdate ? (
                        <Tooltip title={t('action.edit')} placement="top">
                          <IconButton size="small" onClick={() => void openEdit(row)}>
                            <Edit2 size={18} />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {config.canFinalize && row[config.draftField ?? 'draft'] == (config.draftValue ?? 1) ? (
                        <Tooltip
                          title={loyaltyOn && config.resource === 'sales' ? t('crud.finalizeDisabled') : t('crud.finalizeHint', { field: config.finalizeField ?? 'draft', value: String(config.finalizeValue ?? 0) })}
                          placement="top"
                        >
                          <span>
                            <Button
                              size="small"
                              color="warning"
                              disabled={(config.resource === 'sales' && loyaltyOn) || finalizingId === row.id}
                              onClick={() => void finalizeRecord(row)}
                              sx={{ minWidth: 'auto', px: 1.5 }}
                            >
                              {finalizingId === row.id ? '…' : t('action.finalize')}
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}
                      {config.resource === 'reports/registers' && !row.closed_at ? (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          disabled={forceClosing}
                          onClick={() => openForceClose(row)}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {t('registerRecords.forceClose')}
                        </Button>
                      ) : null}
                      {canDelete ? (
                        <Tooltip title={t('action.delete')} placement="top">
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                            <Trash size={18} />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {extraActions.length ? (
                        <Tooltip title={t('action.more')} placement="top">
                          <IconButton
                            size="small"
                            onClick={(event) => setActionMenu({ anchor: event.currentTarget, row })}
                            disabled={Boolean(runningAction && runningAction.endsWith(`:${row.id}`))}
                          >
                            {runningAction && runningAction.endsWith(`:${row.id}`) ? <CircularProgress size={16} /> : <More size={18} />}
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
                );
              })}
              {!rows.length ? (
                <TableRow>
                  <TableCell colSpan={colSpan}>
                    <EmptyState
                      title={canCreate ? t('crud.noRowsYet', { title: tLabel(config.title).toLowerCase() }) : t('crud.noRowsFound', { title: tLabel(config.title).toLowerCase() })}
                      description={canCreate ? t('crud.createFirst', { singular: tLabel(config.singular).toLowerCase() }) : undefined}
                      actionLabel={canCreate ? `${t('action.add')} ${tLabel(config.singular)}` : undefined}
                      actionIcon={canCreate ? Add : undefined}
                      onAction={canCreate ? () => openCreate() : undefined}
                    />
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          </Box>
          <TablePagination
            component="div"
            count={count}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={config.pageSizes ?? [10, 25, 50, 100]}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            onRowsPerPageChange={(event) => {
              setLimit(Number(event.target.value));
              setPage(0);
            }}
          />
        </Box>
      )}

      <Menu anchorEl={actionMenu?.anchor ?? null} open={Boolean(actionMenu)} onClose={() => setActionMenu(null)}>
        {actionMenu
          ? visibleRowActions(actionMenu.row).map((action) => {
              const Icon = action.icon;
              const disabled = action.disabled?.(actionMenu.row) ?? false;
              return (
                <MenuItem key={action.key} disabled={disabled} onClick={() => void runRowAction(action, actionMenu.row)}>
                  {Icon ? (
                    <ListItemIcon sx={{ color: action.color && action.color !== 'inherit' ? `${action.color}.main` : undefined }}>
                      <Icon size={18} />
                    </ListItemIcon>
                  ) : null}
                  <ListItemText primaryTypographyProps={{ color: action.color === 'error' ? 'error' : undefined }}>{tAction(action.label)}</ListItemText>
                </MenuItem>
              );
            })
          : null}
      </Menu>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { maxHeight: '90vh', bgcolor: UI.white } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {editingId ? `${t('action.edit')} ${tLabel(config.singular)}` : `${t('action.add')} ${tLabel(config.singular)}`}
          </Typography>
          <IconButton size="small" onClick={closeDialog}>
            <CloseCircle size={22} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {notice ? <Alert severity="error" sx={{ mb: 2 }}>{notice}</Alert> : null}
          {formErrorSummary.length > 1 ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Stack component="ul" sx={{ pl: 2, m: 0 }}>
                {formErrorSummary.map((message) => (
                  <Typography component="li" key={message} variant="body2">
                    {message}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          ) : null}

          <Box sx={{ display: 'grid', gridTemplateColumns: config.workspaceSummary ? { xs: '1fr', lg: '2fr 1fr' } : '1fr', gap: 2.5 }}>
            {/* Form column */}
            <Stack spacing={2}>
              {fieldsBySection.map((group) => (
                <WorkspaceSection
                  key={group.title}
                  title={group.title ? tLabel(group.title) : t('common.details')}
                  description={config.sectionMeta?.[group.title]?.description}
                >
                  <WorkspaceFieldGrid>
                    {group.fields.map((field) => {
                      const isWide = field.width === 'full' || field.type === 'checkbox' || field.type === 'subform' || field.type === 'textarea' || field.type === 'files' || field.type === 'custom';
                      const serverError = formErrors[field.name]?.[0];
                      const clientError = touchedFields[field.name] ? validateField(field, form[field.name]) : undefined;
                      const fieldError = serverError ?? clientError;
                      const dependent = dependentOptions[field.name];
                      const fieldOptions = field.optionsQuery ? withConfiguredOptions(field, dependent?.options ?? []) : withConfiguredOptions(field, options[field.name] ?? []);
                      return (
                        <Box key={field.name} sx={{ gridColumn: isWide ? '1 / -1' : undefined }}>
                          {field.type === 'custom' && field.render ? (
                            field.render({ field, value: form[field.name], form, editing, error: fieldError, onChange: (value) => updateField(field.name, value), setField: updateField })
                          ) : (
                            <CrudFieldInput
                              field={field}
                              value={form[field.name]}
                              error={fieldError}
                              options={fieldOptions}
                              loadingOptions={Boolean(field.optionsEndpoint && !options[field.name])}
                              onChange={(value) => updateField(field.name, value)}
                              optionMap={options}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </WorkspaceFieldGrid>
                </WorkspaceSection>
              ))}
              {config.customFields && customFieldDefs.length ? (
                <WorkspaceSection title={t('crud.customFields')}>
                  <WorkspaceFieldGrid>
                    {customFieldDefs.map((definition) => (
                      <Box key={definition.slug} sx={{ gridColumn: definition.type === 'textarea' ? '1 / -1' : undefined }}>
                        <CustomFieldInput
                          definition={definition}
                          value={normalizeExtraAttributes(form.extra_attributes)[definition.slug]}
                          error={formErrors[`extra_attributes.${definition.slug}`]?.[0]}
                          onChange={(value) => updateCustomField(definition.slug, value)}
                        />
                      </Box>
                    ))}
                  </WorkspaceFieldGrid>
                </WorkspaceSection>
              ) : null}
            </Stack>

            {/* Contextual sidebar (desktop only) */}
            {config.workspaceSummary ? (
              <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <WorkspaceSummary chips={config.workspaceSummary(form) ?? []} />
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', justifyContent: 'space-between' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            <kbd style={{ fontFamily: 'inherit', padding: '1px 5px', border: `1px solid ${UI.borderStrong}`, borderRadius: 4, fontSize: '11px' }}>⌘S</kbd> {t('common.pressToSave')}
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button onClick={closeDialog} disabled={saving} sx={{ color: 'text.secondary' }}>
              {t('action.cancel')}
            </Button>
            <Button onClick={() => void save()} variant="contained" disabled={saving || hasPendingOptions} sx={{ minWidth: 100 }}>
              {saving ? t('action.saving') : hasPendingOptions ? t('common.loading') : t('action.save')}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {CustomForm && customFormState ? (
        <CustomForm
          config={config}
          recordId={customFormState.recordId}
          prefill={customFormState.prefill}
          onClose={() => setCustomFormState(null)}
          onSaved={() => {
            const wasEditing = customFormState.recordId != null;
            setCustomFormState(null);
            setToast({ message: `${tLabel(config.singular)} ${wasEditing ? t('action.updated') : t('action.created')}.`, severity: 'success' });
            void loadRows();
          }}
        />
      ) : null}

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>{t('crud.deleteConfirm', { singular: tLabel(config.singular) })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('crud.deleteMessage', { singular: tLabel(config.singular).toLowerCase(), name: deleteLabel })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('action.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => void remove()} disabled={deleting}>
            {deleting ? t('action.deleting') : t('action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkConfirmOpen} onClose={() => !deleting && setBulkConfirmOpen(false)}>
        <DialogTitle>{t('action.deleteSelected')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('crud.bulkDeleteMessage', { count: selected.size })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkConfirmOpen(false)} disabled={deleting}>
            {t('action.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => void removeSelected()} disabled={deleting}>
            {deleting ? t('action.deleting') : t('action.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmEntry)} onClose={() => { confirmEntry?.resolve(false); setConfirmEntry(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmEntry?.title ?? t('common.confirm')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmEntry?.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { confirmEntry?.resolve(false); setConfirmEntry(null); }}>{t('action.cancel')}</Button>
          <Button
            variant="contained"
            color={confirmEntry?.color ?? 'primary'}
            onClick={() => { confirmEntry?.resolve(true); setConfirmEntry(null); }}
          >
            {confirmEntry?.confirmLabel ?? t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {dialogs.map((entry) => {
        const close = () => setDialogs((current) => current.filter((item) => item.id !== entry.id));
        return (
          <Dialog key={entry.id} open onClose={close} fullWidth maxWidth={entry.maxWidth ?? 'md'}>
            {entry.title ? (
              <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>{entry.title}</Typography>
                <IconButton size="small" onClick={close}>
                  <CloseCircle size={22} />
                </IconButton>
              </DialogTitle>
            ) : null}
            <DialogContent dividers={Boolean(entry.title)}>{entry.content(close)}</DialogContent>
          </Dialog>
        );
      })}

      <Dialog open={Boolean(forceCloseTarget)} onClose={closeForceCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('registerRecords.forceCloseTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <DialogContentText>{t('registerRecords.forceCloseWarning')}</DialogContentText>
            {forceCloseError ? <Alert severity="error">{forceCloseError}</Alert> : null}
            <TextField
              label={t('field.comment')}
              value={forceCloseComment}
              onChange={(event) => setForceCloseComment(event.target.value)}
              multiline
              minRows={3}
              fullWidth
              disabled={forceClosing}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForceCloseDialog} disabled={forceClosing}>
            {t('action.cancel')}
          </Button>
          <Button color="error" variant="contained" onClick={() => void forceCloseRegister()} disabled={forceClosing}>
            {forceClosing ? t('registerRecords.forceClosing') : t('registerRecords.forceCloseConfirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 640 },
            bgcolor: UI.white,
            borderLeft: `1px solid ${UI.border}`
          }
        }}
      >
        <Stack spacing={2.5} sx={{ p: 3 }}>
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {detailTitle}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {t('common.detail', { name: tLabel(config.singular) })}
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => setDetailOpen(false)}>
              {t('action.close')}
            </Button>
          </Stack>
          {detailRecord && !detailLoading ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {canUpdate ? (
                <Button size="small" variant="outlined" startIcon={<Edit2 size={16} />} onClick={() => { setDetailOpen(false); void openEdit(detailRecord); }}>
                  {t('action.edit')}
                </Button>
              ) : null}
              {visibleRowActions(detailRecord).map((action) => {
                const Icon = action.icon;
                return (
                  <Button
                    key={action.key}
                    size="small"
                    variant="outlined"
                    color={action.color && action.color !== 'inherit' ? action.color : 'primary'}
                    startIcon={Icon ? <Icon size={16} /> : undefined}
                    disabled={action.disabled?.(detailRecord) ?? false}
                    onClick={() => void runRowAction(action, detailRecord)}
                  >
                    {tAction(action.label)}
                  </Button>
                );
              })}
            </Stack>
          ) : null}
          <Divider />
          {detailLoading ? <LoadingBlock /> : null}
          {detailError ? <Alert severity="error">{detailError}</Alert> : null}
          {!detailLoading && detailRecord ? (
            <Stack spacing={2.5}>
              {(config.detailSections ?? []).map((section) => (
                <DetailSection key={section.title} section={section} record={detailRecord} />
              ))}
              <ExtraAttributesSection record={detailRecord} />
              {DetailExtra ? <DetailExtra record={detailRecord} reload={async () => { await openDetail(detailRecord); }} ctx={actionContext} /> : null}
              {config.showAttachments ? (
                <AttachmentsSection
                  record={detailRecord}
                  canDelete={canUpdate}
                  onDeleted={() => void openDetail(detailRecord)}
                  onError={(message) => setToast({ message, severity: 'error' })}
                />
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </Drawer>

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? (
          <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Stack>
  );
}

// Custom fields defined under Settings → Fields (extra_attributes). Options
// arrive as a pipe-separated string ("a|b|c").
export function CustomFieldInput({
  definition,
  value,
  error,
  onChange
}: {
  definition: CustomFieldDefinition;
  value: unknown;
  error?: string;
  onChange: (value: unknown) => void;
}) {
  const required = Boolean(Number(definition.required));
  const fieldOptions = String(definition.options ?? '')
    .split('|')
    .map((option) => option.trim())
    .filter(Boolean);
  if (definition.type === 'checkbox' && fieldOptions.length) {
    const current = Array.isArray(value) ? value.map(String) : value ? String(value).split(',') : [];
    return (
      <FormControl error={Boolean(error)} required={required}>
        <Typography variant="body2" color="text.secondary">{definition.name}</Typography>
        <Stack direction="row" flexWrap="wrap">
          {fieldOptions.map((option) => (
            <FormControlLabel
              key={option}
              control={
                <Checkbox
                  size="small"
                  checked={current.includes(option)}
                  onChange={(event) => onChange(event.target.checked ? [...current, option] : current.filter((entry) => entry !== option))}
                />
              }
              label={option}
            />
          ))}
        </Stack>
        {error ? <Typography variant="caption" color="error">{error}</Typography> : null}
      </FormControl>
    );
  }
  if (definition.type === 'checkbox' || definition.type === 'boolean') {
    return <FormControlLabel control={<Checkbox checked={Boolean(Number(value)) || value === true} onChange={(event) => onChange(event.target.checked ? 1 : 0)} />} label={definition.name} />;
  }
  if (definition.type === 'select' || definition.type === 'radio') {
    return (
      <TextField select fullWidth label={definition.name} required={required} value={(value ?? '') as string} error={Boolean(error)} helperText={error} onChange={(event) => onChange(event.target.value)}>
        <MenuItem value="">{t('common.none')}</MenuItem>
        {fieldOptions.map((option) => (
          <MenuItem key={option} value={option}>{option}</MenuItem>
        ))}
      </TextField>
    );
  }
  const inputType = definition.type === 'number' ? 'number' : definition.type === 'date' ? 'date' : definition.type === 'email' ? 'email' : 'text';
  return (
    <TextField
      fullWidth
      label={definition.name}
      required={required}
      type={inputType}
      multiline={definition.type === 'textarea'}
      minRows={definition.type === 'textarea' ? 3 : undefined}
      value={(value ?? '') as string}
      error={Boolean(error)}
      helperText={error}
      InputLabelProps={inputType === 'date' ? { shrink: true } : undefined}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function ExtraAttributesSection({ record }: { record: CrudRecord }) {
  const extras = normalizeExtraAttributes(record.extra_attributes);
  const entries = Object.entries(extras).filter(([, value]) => value != null && value !== '');
  if (!entries.length) return null;
  return (
    <DetailSection
      section={{
        title: 'Custom Fields',
        fields: entries.map(([key]) => ({ key, label: key, render: () => {
          const value = extras[key];
          return Array.isArray(value) ? value.join(', ') : String(value);
        } }))
      }}
      record={record}
    />
  );
}

type AttachmentRecord = { id?: string | number; uuid?: string; name?: string; file_name?: string; url?: string; original_url?: string; preview_url?: string; mime_type?: string };

export function AttachmentsSection({
  record,
  canDelete,
  onDeleted,
  onError
}: {
  record: CrudRecord;
  canDelete: boolean;
  onDeleted: () => void;
  onError: (message: string) => void;
}) {
  const attachments = Array.isArray(record.attachments) ? (record.attachments as AttachmentRecord[]) : [];
  const [busy, setBusy] = useState<string | null>(null);
  return (
    <Box sx={{ border: `1px solid ${UI.border}`, borderRadius: '10px', overflow: 'hidden', bgcolor: UI.white }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: UI.accentAlpha(0.06), borderBottom: `1px solid ${UI.border}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: UI.ink }}>
          {t('crud.attachments')}
        </Typography>
      </Box>
      {attachments.length ? (
        <Stack divider={<Divider />}>
          {attachments.map((attachment) => {
            const key = String(attachment.uuid ?? attachment.id);
            const href = attachment.original_url ?? attachment.url;
            return (
              <Stack key={key} direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {href ? (
                    <a href={href} target="_blank" rel="noreferrer">
                      {attachment.name ?? attachment.file_name ?? key}
                    </a>
                  ) : (
                    attachment.name ?? attachment.file_name ?? key
                  )}
                </Typography>
                {canDelete ? (
                  <IconButton
                    size="small"
                    color="error"
                    disabled={busy === key}
                    onClick={async () => {
                      setBusy(key);
                      try {
                        await deleteAttachment(attachment.uuid ?? attachment.id ?? '');
                        onDeleted();
                      } catch (err) {
                        onError(apiErrorMessage(err, t('common.deleteFailed')));
                      } finally {
                        setBusy(null);
                      }
                    }}
                  >
                    <Trash size={16} />
                  </IconButton>
                ) : null}
              </Stack>
            );
          })}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          {t('crud.noAttachments')}
        </Typography>
      )}
    </Box>
  );
}

function groupFields(fields: CrudField[]) {
  const groups: Array<{ title: string; fields: CrudField[] }> = [];
  fields.forEach((field) => {
    const title = field.section ?? '';
    let group = groups.find((candidate) => candidate.title === title);
    if (!group) {
      group = { title, fields: [] };
      groups.push(group);
    }
    group.fields.push(field);
  });
  return groups;
}

function DetailSection({ section, record }: { section: CrudDetailSection; record: CrudRecord }) {
  const rendered = section.render?.(record);
  return (
    <Box sx={{ border: `1px solid ${UI.border}`, borderRadius: '10px', overflow: 'hidden', bgcolor: UI.white, boxShadow: UI.shadowSm }}>
      <Box sx={{ px: 2, py: 1.25, bgcolor: UI.accentAlpha(0.06), borderBottom: `1px solid ${UI.border}` }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: UI.ink }}>
          {tLabel(section.title)}
        </Typography>
      </Box>
      {rendered ? (
        <Typography sx={{ p: 2, whiteSpace: 'pre-wrap' }} variant="body2">
          {rendered}
        </Typography>
      ) : (
        <Table size="small">
          <TableBody>
            {(section.fields ?? []).map((field) => (
              <TableRow key={field.key}>
                <TableCell sx={{ width: 180, color: 'text.secondary' }}>{tLabel(field.label)}</TableCell>
                <TableCell>{detailValue(record, field)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

function CrudFieldInput({
  field,
  value,
  error,
  options,
  loadingOptions,
  optionMap,
  onChange
}: {
  field: CrudField;
  value: unknown;
  error?: string;
  options: SelectOption[];
  loadingOptions: boolean;
  optionMap: Record<string, SelectOption[]>;
  onChange: (value: unknown) => void;
}) {
  if (field.type === 'checkbox') {
    return (
      <FormControlLabel
        control={<Checkbox checked={Boolean(value)} onChange={(event) => onChange(event.target.checked)} />}
        label={tLabel(field.label)}
      />
    );
  }

  if (field.qrScan) {
    return (
      <QrScanInput
        label={tLabel(field.label)}
        value={value}
        error={error}
        helperText={field.helperText}
        onChange={onChange}
      />
    );
  }

  if (field.type === 'select' || field.type === 'multiselect') {
    // Use a searchable Combobox for endpoint-backed fields (datasets that can grow);
    // keep the plain Select for small static option sets.
    if (field.optionsEndpoint) {
      return (
        <Combobox
          label={tLabel(field.label)}
          value={field.type === 'multiselect' ? (Array.isArray(value) ? value : []) : ((value as string | number | undefined) ?? '')}
          options={options}
          multiple={field.type === 'multiselect'}
          onChange={onChange}
          error={error}
          helperText={field.helperText}
          required={field.required}
          loading={loadingOptions}
          disabled={field.readOnly}
        />
      );
    }
    const selected = field.type === 'multiselect' ? (Array.isArray(value) ? value : []) : value ?? '';
    return (
      <FormControl fullWidth error={Boolean(error)} required={field.required}>
        <InputLabel>{tLabel(field.label)}</InputLabel>
        <Select
          multiple={field.type === 'multiselect'}
          label={tLabel(field.label)}
          value={selected as string | number | Array<string | number>}
          onChange={(event) => onChange(event.target.value)}
          disabled={loadingOptions || Boolean(field.readOnly)}
        >
          {field.type === 'select' ? <MenuItem value="">{t('common.none')}</MenuItem> : null}
          {options.map((option) => (
            <MenuItem key={String(option.value)} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {error || field.helperText ? (
          <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, ml: 1.75 }}>
            {loadingOptions ? 'Loading options...' : error ?? field.helperText}
          </Typography>
        ) : null}
      </FormControl>
    );
  }

  if (field.type === 'subform') {
    return (
      <SubformInput
        field={field}
        value={Array.isArray(value) ? (value as Array<Record<string, unknown>>) : []}
        optionMap={optionMap}
        onChange={(next) => onChange(next)}
      />
    );
  }

  if (field.type === 'file' || field.type === 'files') {
    return <FileFieldInput field={field} value={value} error={error} onChange={onChange} />;
  }

  if (field.type === 'datetime' || field.type === 'time') {
    const raw = (value ?? '') as string;
    // datetime-local wants "YYYY-MM-DDTHH:mm"; the API speaks "YYYY-MM-DD HH:mm:ss".
    const shown = field.type === 'datetime' ? raw.replace(' ', 'T').slice(0, 16) : raw.slice(0, 5);
    return (
      <TextField
        label={tLabel(field.label)}
        type={field.type === 'datetime' ? 'datetime-local' : 'time'}
        value={shown}
        onChange={(event) => {
          const next = event.target.value;
          if (!next) return onChange('');
          onChange(field.type === 'datetime' ? `${next.replace('T', ' ')}:00` : `${next}:00`);
        }}
        required={field.required}
        fullWidth
        disabled={field.readOnly}
        error={Boolean(error)}
        helperText={error ?? field.helperText}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  if (field.type === 'date') {
    return (
      <TextField
        label={tLabel(field.label)}
        type="date"
        value={String(value ?? '').slice(0, 10)}
        onChange={(event) => onChange(event.target.value)}
        required={field.required}
        fullWidth
        error={Boolean(error)}
        helperText={error ?? field.helperText}
        InputLabelProps={{ shrink: true }}
      />
    );
  }

  if (field.type === 'password') {
    return (
      <PasswordInput
        label={tLabel(field.label)}
        value={(value ?? '') as string}
        onChange={onChange}
        required={field.required}
        error={error}
        helperText={field.helperText}
      />
    );
  }

  return (
    <TextField
      label={tLabel(field.label)}
      type={field.type === 'number' ? 'number' : field.type === 'color' ? 'color' : 'text'}
      value={(value ?? '') as string | number}
      onChange={(event) => onChange(field.type === 'number' && event.target.value !== '' ? Number(event.target.value) : event.target.value)}
      required={field.required}
      fullWidth
      disabled={field.readOnly}
      multiline={field.type === 'textarea' || field.multiline}
      minRows={field.type === 'textarea' || field.multiline ? 3 : undefined}
      error={Boolean(error)}
      helperText={error ?? field.helperText}
      InputLabelProps={field.type === 'color' ? { shrink: true } : undefined}
    />
  );
}

// Repeatable inline sub-records (e.g. item portions/variations). Each row holds a
// small object built from field.subformFields. Add/remove rows; values bubble up.
function SubformInput({
  field,
  value,
  optionMap,
  onChange
}: {
  field: CrudField;
  value: Array<Record<string, unknown>>;
  optionMap: Record<string, SelectOption[]>;
  onChange: (next: Array<Record<string, unknown>>) => void;
}) {
  const subFields = field.subformFields ?? [];

  if (field.itemSearchEndpoint && field.itemResolveEndpoint) {
    return (
      <ItemLineSubform
        field={field}
        value={value}
        onChange={onChange}
      />
    );
  }

  function emptyRow() {
    return subFields.reduce<Record<string, unknown>>((row, sub) => {
      row[sub.name] = sub.defaultValue !== undefined ? sub.defaultValue : sub.type === 'number' ? 0 : sub.type === 'multiselect' ? [] : '';
      return row;
    }, {});
  }

  function updateRow(index: number, key: string, cellValue: unknown) {
    const next = value.map((row, i) => (i === index ? { ...row, [key]: cellValue } : row));
    onChange(next);
  }

  function addRow() {
    onChange([...value, emptyRow()]);
  }

  function removeRow(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        {field.label}
      </Typography>
      {field.helperText ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {field.helperText}
        </Typography>
      ) : null}
      <Stack spacing={1.5}>
        {value.map((row, index) => (
          <Stack key={index} direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap">
            {subFields.map((sub) => {
              const subOptions = withConfiguredOptions(sub, optionMap[sub.name] ?? []);
              const subLoading = Boolean(sub.optionsEndpoint && !optionMap[sub.name]);
              if (sub.type === 'select' || sub.type === 'multiselect') {
                if (sub.optionsEndpoint) {
                  return (
                    <Box key={sub.name} sx={{ minWidth: 240, flex: '2 1 260px' }}>
                      <Combobox
                        label={tLabel(sub.label)}
                        value={sub.type === 'multiselect' ? (Array.isArray(row[sub.name]) ? (row[sub.name] as Array<string | number>) : []) : ((row[sub.name] as string | number | undefined) ?? '')}
                        options={subOptions}
                        multiple={sub.type === 'multiselect'}
                        onChange={(next) => updateRow(index, sub.name, sub.type === 'multiselect' ? normalizeAllSelection(row[sub.name], next) : next)}
                        helperText={sub.helperText}
                        required={sub.required}
                        loading={subLoading}
                      />
                    </Box>
                  );
                }
                const selected = sub.type === 'multiselect' ? (Array.isArray(row[sub.name]) ? row[sub.name] : []) : row[sub.name] ?? '';
                return (
                  <FormControl key={sub.name} size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
                    <InputLabel>{tLabel(sub.label)}</InputLabel>
                    <Select
                      multiple={sub.type === 'multiselect'}
                      label={tLabel(sub.label)}
                      value={selected as string | number | Array<string | number>}
                      disabled={subLoading}
                      onChange={(event) => updateRow(index, sub.name, sub.type === 'multiselect' ? normalizeAllSelection(row[sub.name], event.target.value) : event.target.value)}
                    >
                      {sub.type === 'select' ? <MenuItem value="">{t('common.none')}</MenuItem> : null}
                      {subOptions.map((opt) => (
                        <MenuItem key={String(opt.value)} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                    {sub.helperText ? (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                        {subLoading ? t('common.loading') : sub.helperText}
                      </Typography>
                    ) : null}
                  </FormControl>
                );
              }
              // Read-only fields (e.g. auto-generated register codes).
              if (sub.readOnly) {
                return (
                  <TextField
                    key={sub.name}
                    label={tLabel(sub.label)}
                    type={sub.type === 'number' ? 'number' : 'text'}
                    value={(row[sub.name] ?? '') as string | number}
                    onChange={() => { /* read-only */ }}
                    required={sub.required}
                    size="small"
                    disabled
                    helperText={sub.helperText}
                    sx={{ minWidth: 120, flex: '1 1 120px' }}
                  />
                );
              }
              return (
                <TextField
                  key={sub.name}
                  label={tLabel(sub.label)}
                  type={sub.type === 'number' ? 'number' : 'text'}
                  value={(row[sub.name] ?? '') as string | number}
                  onChange={(event) =>
                    updateRow(index, sub.name, sub.type === 'number' && event.target.value !== '' ? Number(event.target.value) : event.target.value)
                  }
                  required={sub.required}
                  size="small"
                  sx={{ minWidth: 120, flex: '1 1 120px' }}
                />
              );
            })}
            <Button size="small" color="error" onClick={() => removeRow(index)} sx={{ mt: 0.5 }}>
              {t('action.remove')}
            </Button>
          </Stack>
        ))}
        <Box>
          <Button size="small" variant="outlined" onClick={addRow}>
            Add {field.label.replace(/s$/, '').toLowerCase()}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

function ItemLineSubform({
  field,
  value,
  onChange
}: {
  field: CrudField;
  value: Array<Record<string, unknown>>;
  onChange: (next: Array<Record<string, unknown>>) => void;
}) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<ItemSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const search = query.trim();
    if (!field.itemSearchEndpoint || search.length < 2 || search.includes('(')) {
      setOptions([]);
      setLoading(false);
      return;
    }

    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(null);
      ensureSessionLocationSelected()
        .then(() => adminApi.get(field.itemSearchEndpoint!, { params: { q: search } }))
        .then((response) => {
          if (!active) return;
          setOptions(Array.isArray(response.data) ? response.data : response.data ? [response.data] : []);
        })
        .catch((err) => {
          if (!active) return;
          setOptions([]);
          setError(apiErrorMessage(err, 'Unable to search items.'));
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [field.itemSearchEndpoint, query]);

  function updateRow(index: number, key: string, cellValue: unknown) {
    onChange(value.map((row, i) => (i === index ? { ...row, [key]: cellValue } : row)));
  }

  function removeRow(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  async function addItem(item: ItemSearchResult | null) {
    if (!item || !field.itemResolveEndpoint) return;
    setAdding(true);
    setError(null);
    try {
      await ensureSessionLocationSelected();
      const response = await adminApi.get(`${field.itemResolveEndpoint}/${item.id}`);
      const line = itemLineFromRecord(response.data as Record<string, unknown>);
      const existingIndex = value.findIndex((row) => String(row.item_id) === String(line.item_id));
      if (existingIndex >= 0) {
        onChange(value.map((row, index) => (
          index === existingIndex
            ? { ...row, quantity: numericValue(row.quantity) + 1 }
            : row
        )));
      } else {
        onChange([...value, line]);
      }
      setQuery('');
      setOptions([]);
    } catch (err) {
      setError(apiErrorMessage(err, t('common.unableToLoad', { name: 'item' })));
    } finally {
      setAdding(false);
    }
  }

  const itemCount = value.length;
  const quantityTotal = value.reduce((sum, row) => sum + numericValue(row.quantity), 0);
  const payableTotal = value.reduce((sum, row) => sum + numericValue(row.cost) * numericValue(row.quantity), 0);

  return (
    <Box sx={{ width: '100%' }}>
      {field.helperText ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {field.helperText}
        </Typography>
      ) : null}
      <Autocomplete
        freeSolo={false}
        options={options}
        loading={loading || adding}
        value={null}
        inputValue={query}
        onInputChange={(_event, next) => setQuery(next)}
        onChange={(_event, selected) => void addItem(selected as ItemSearchResult | null)}
        getOptionLabel={(option) => (typeof option === 'string' ? option : itemSearchLabel(option))}
        isOptionEqualToValue={(option, selected) => String(option.id) === String(selected.id)}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={field.itemSearchPlaceholder ?? t('order.searchOrScanBarcode')}
            size="small"
            error={Boolean(error)}
            helperText={error ?? undefined}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && options.length === 1) {
                event.preventDefault();
                void addItem(options[0]);
              }
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading || adding ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        sx={{ mb: 1.5 }}
      />

      {value.length ? (
        <Table size="small" sx={{ border: 1, borderColor: 'divider' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ width: 56, fontWeight: 700 }}>#</TableCell>
              <TableCell sx={{ width: 52 }} />
              <TableCell sx={{ fontWeight: 700 }}>{t('field.description')}</TableCell>
              <TableCell align="right" sx={{ width: 160, fontWeight: 700 }}>{tLabel('Cost')}</TableCell>
              <TableCell align="right" sx={{ width: 170, fontWeight: 700 }}>{tLabel('Quantity')}</TableCell>
              <TableCell align="right" sx={{ width: 170, fontWeight: 700 }}>{t('field.subtotal')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {value.map((row, index) => {
              const quantity = numericValue(row.quantity);
              const cost = numericValue(row.cost);
              return (
                <TableRow key={`${row.item_id ?? row.id ?? index}`}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => removeRow(index)} aria-label={t('order.removeItem')}>
                      <Trash size={18} />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{String(row.name ?? '-')}</Typography>
                    <Typography variant="caption" color="text.secondary">{String(row.code ?? '')}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      value={cost}
                      onChange={(event) => updateRow(index, 'cost', Number(event.target.value || 0))}
                      size="small"
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      value={quantity}
                      onChange={(event) => updateRow(index, 'quantity', Number(event.target.value || 0))}
                      size="small"
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{moneySummary(cost * quantity)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t('order.emptyTitle')}</Typography>
          <Typography variant="body2">{t('order.emptyDescription')}</Typography>
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1, mt: 1.5, p: 1.25, border: 1, borderColor: 'warning.light', bgcolor: 'rgba(255, 248, 225, 0.76)' }}>
        <Typography variant="body2">{t('field.items')}: <strong>{t('order.itemQuantitySummary', { items: itemCount, quantity: moneySummary(quantityTotal).replace('.00', '') })}</strong></Typography>
        <Typography variant="body2">{t('field.tax')}: <strong>{moneySummary(0)}</strong></Typography>
        <Typography variant="body2">{t('order.payableAmount')}: <strong>{moneySummary(payableTotal)}</strong></Typography>
      </Box>
    </Box>
  );
}

// File input. Single files keep either a File (new upload) or the existing URL
// string (shown as a link/preview, never sent back). Multiple files hold File[].
function FileFieldInput({
  field,
  value,
  error,
  onChange
}: {
  field: CrudField;
  value: unknown;
  error?: string;
  onChange: (value: unknown) => void;
}) {
  const multiple = field.type === 'files';
  const files = multiple ? (Array.isArray(value) ? (value as unknown[]).filter((entry) => typeof File !== 'undefined' && entry instanceof File) as File[] : []) : [];
  const single = !multiple ? value : null;
  const isNewFile = typeof File !== 'undefined' && single instanceof File;
  const existingUrl = !multiple && typeof single === 'string' && single ? single : null;
  const previewUrl = useMemo(() => (isNewFile ? URL.createObjectURL(single as File) : existingUrl), [existingUrl, isNewFile, single]);
  const isImage = (field.accept ?? '').includes('image') || /\.(png|jpe?g|gif|svg|webp)(\?|$)/i.test(existingUrl ?? '') || (isNewFile && (single as File).type.startsWith('image/'));

  return (
    <Box>
      <Typography variant="body2" color={error ? 'error' : 'text.secondary'} sx={{ mb: 0.75 }}>
        {tLabel(field.label)}
        {field.required ? ' *' : ''}
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        {!multiple && previewUrl && isImage ? (
          <Box component="img" src={previewUrl} alt="" sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider' }} />
        ) : null}
        <Button component="label" variant="outlined" size="small" startIcon={<DocumentUpload size={16} />} disabled={field.readOnly}>
          {multiple ? t('action.chooseFiles') : t('action.chooseFile')}
          <input
            hidden
            type="file"
            multiple={multiple}
            accept={field.accept}
            onChange={(event) => {
              const picked = Array.from(event.target.files ?? []);
              if (multiple) onChange([...files, ...picked]);
              else onChange(picked[0] ?? single ?? '');
              event.target.value = '';
            }}
          />
        </Button>
        {!multiple && isNewFile ? <Typography variant="caption">{(single as File).name}</Typography> : null}
        {!multiple && existingUrl ? (
          <Typography variant="caption">
            <a href={existingUrl} target="_blank" rel="noreferrer">{t('action.view')}</a>
          </Typography>
        ) : null}
        {!multiple && (isNewFile || existingUrl) ? (
          <Button size="small" color="error" onClick={() => onChange('')}>{t('action.remove')}</Button>
        ) : null}
      </Stack>
      {multiple && files.length ? (
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          {files.map((file, index) => (
            <Stack key={`${file.name}-${index}`} direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" sx={{ flex: 1 }}>{file.name}</Typography>
              <IconButton size="small" color="error" onClick={() => onChange(files.filter((_, i) => i !== index))}>
                <Trash size={14} />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      ) : null}
      {error || field.helperText ? (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
          {error ?? field.helperText}
        </Typography>
      ) : null}
    </Box>
  );
}

// Password input with show/hide eye toggle — matches legacy app's eye-icon behavior.
function PasswordInput({
  label,
  value,
  onChange,
  required,
  error,
  helperText
}: {
  label: string;
  value: string;
  onChange: (value: unknown) => void;
  required?: boolean;
  error?: string;
  helperText?: string;
}) {
  const [showPassword, setShowPassword] = useState(false);
  return (
    <TextField
      label={label}
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      required={required}
      fullWidth
      error={Boolean(error)}
      helperText={error ?? helperText}
      autoComplete="new-password"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              onClick={() => setShowPassword((show) => !show)}
              edge="end"
            >
              {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );
}
